import {
  getTransportOperationName,
  transportOperations as transportOperationDefinitions,
  type TransportOperationDefinition,
  type TransportOperationInput,
  type TransportOperationName,
  type TransportOperationResponse,
  type TransportPathParamValue,
  type TransportResolvedRequest,
} from "@/types_api/backend/transport/operations"

import { TRANSPORT_BASE } from "../config/transportUrls"
import { applyWorkspaceRequestHeader } from "./workspaceRequestHeaders"

type PrimitiveQueryValue = boolean | number | string
type QueryValue = PrimitiveQueryValue | PrimitiveQueryValue[] | null | undefined
type QueryParams = Record<string, QueryValue>
type FetchLike = typeof fetch
const TRANSPORT_REQUEST_CREDENTIALS: RequestCredentials = "omit"
const ORGANIZATION_SLUG_HEADER = "X-Coval-Organization-Slug"

interface TransportErrorDetail {
  code?: string
  details?: unknown[]
  message?: string
}

interface TransportOrganizationContext {
  slug?: string | null
}

interface TransportClientOptions {
  baseUrl?: string
  fetch?: FetchLike
  getToken?: () => Promise<string | null> | string | null
  getOrganizationContext?: () => TransportOrganizationContext | null
  getWorkspaceId?: () => string | null
}

export interface TransportCallExecutionOptions {
  headers?: HeadersInit
  signal?: AbortSignal
}

type TransportCallArgs<Name extends TransportOperationName> =
  TransportResolvedRequest<Name> extends never
    ? [
        operation: TransportOperationInput<Name>,
        options?: TransportCallExecutionOptions,
      ]
    : [
        operation: TransportOperationInput<Name>,
        request: TransportResolvedRequest<Name>,
        options?: TransportCallExecutionOptions,
      ]

type TransportOperationMethods = {
  [Name in TransportOperationName]: TransportResolvedRequest<Name> extends never
    ? (
        options?: TransportCallExecutionOptions,
      ) => Promise<TransportOperationResponse<Name>>
    : (
        request: TransportResolvedRequest<Name>,
        options?: TransportCallExecutionOptions,
      ) => Promise<TransportOperationResponse<Name>>
}

export class TransportRequestError extends Error {
  status: number
  responseBody?: string
  detail?: TransportErrorDetail
  durationMs?: number

  constructor(
    message: string,
    status: number,
    responseBody?: string,
    detail?: TransportErrorDetail,
    durationMs?: number,
  ) {
    super(message)
    this.name = "TransportRequestError"
    this.status = status
    this.responseBody = responseBody
    this.detail = detail
    this.durationMs = durationMs
  }
}

export class TransportAuthTokenError extends Error {
  cause?: unknown
  operation: TransportOperationName

  constructor(operation: TransportOperationName, cause?: unknown) {
    super(`Transport operation "${operation}" failed to acquire auth token.`)
    this.name = "TransportAuthTokenError"
    this.operation = operation
    this.cause = cause
  }
}

export class TransportAuthRedirectError extends TransportRequestError {
  responseUrl?: string

  constructor(
    operation: TransportOperationName,
    status: number,
    responseBody?: string,
    durationMs?: number,
    responseUrl?: string,
  ) {
    super(
      `Transport operation "${operation}" was redirected to sign-in with status ${status}.`,
      status,
      responseBody,
      undefined,
      durationMs,
    )
    this.name = "TransportAuthRedirectError"
    this.responseUrl = responseUrl
  }
}

export class TransportContextNotReadyError extends TransportRequestError {
  constructor(operation: TransportOperationName) {
    super(
      `Transport operation "${operation}" is waiting for auth/org context.`,
      0,
    )
  }
}

function isSignInUrl(responseUrl: string) {
  try {
    const pathname = new URL(responseUrl).pathname.replace(/\/+$/, "")
    return pathname === "/signin" || pathname === "/sign-in"
  } catch (_error: unknown) {
    return false
  }
}

function isLikelyAuthRedirectResponse(
  operation: TransportOperationDefinition,
  response: Response,
) {
  const contentType = response.headers.get("Content-Type")?.toLowerCase() ?? ""
  return (
    operation.auth === "session" &&
    response.redirected &&
    contentType.includes("text/html") &&
    isSignInUrl(response.url)
  )
}

function appendQueryParams(url: URL, query?: QueryParams) {
  if (!query) {
    return
  }

  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) {
      continue
    }

    const values = Array.isArray(value) ? value : [value]

    for (const item of values) {
      url.searchParams.append(key, String(item))
    }
  }
}

async function getErrorBody(response: Response) {
  try {
    return await response.text()
  } catch (_error: unknown) {
    return undefined
  }
}

function getTransportErrorDetail(
  responseBody: string | undefined,
): TransportErrorDetail | undefined {
  if (!responseBody) {
    return undefined
  }

  try {
    const parsed = JSON.parse(responseBody) as unknown
    if (typeof parsed !== "object" || parsed === null || !("error" in parsed)) {
      return undefined
    }

    const error = parsed.error
    if (typeof error !== "object" || error === null) {
      return undefined
    }

    const code =
      "code" in error && typeof error.code === "string" ? error.code : undefined
    const message =
      "message" in error && typeof error.message === "string"
        ? error.message
        : undefined
    const details =
      "details" in error && Array.isArray(error.details)
        ? error.details
        : undefined

    if (!code && !message && !details) {
      return undefined
    }

    return { code, details, message }
  } catch (_error: unknown) {
    return undefined
  }
}

function getRequestRecord(request: unknown) {
  if (request === undefined) {
    return {}
  }

  if (
    typeof request !== "object" ||
    request === null ||
    Array.isArray(request)
  ) {
    throw new Error("Transport requests must be plain objects.")
  }

  return { ...request } as Record<string, unknown>
}

function resolvePath(
  path: string,
  pathParams: readonly string[],
  requestRecord: Record<string, unknown>,
) {
  let resolvedPath = path

  for (const pathParam of pathParams) {
    const value = requestRecord[pathParam]

    if (value === undefined || value === null) {
      throw new Error(
        `Missing required transport path param "${pathParam}" for ${path}.`,
      )
    }

    resolvedPath = resolvedPath.replace(
      `{${pathParam}}`,
      encodeURIComponent(getPathParamValue(value)),
    )

    delete requestRecord[pathParam]
  }

  return resolvedPath
}

function getPathParamValue(value: unknown): string {
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    const pathParamValue: TransportPathParamValue = value
    return String(pathParamValue)
  }

  throw new Error(
    "Transport path params must be strings, numbers, or booleans.",
  )
}

function getNowMs() {
  return typeof globalThis.performance?.now === "function"
    ? globalThis.performance.now()
    : Date.now()
}

async function parseResponse<TResponse>(
  operation: TransportOperationName,
  response: Response,
  durationMs: number,
) {
  if (response.status === 204) {
    return undefined as TResponse
  }

  const responseText = await response.text()
  if (!responseText) {
    return undefined as TResponse
  }

  try {
    return JSON.parse(responseText) as TResponse
  } catch (error: unknown) {
    const parseErrorMessage =
      error instanceof Error ? error.message : "Unknown JSON parse error"

    throw new TransportRequestError(
      `Transport operation "${operation}" response parsing failed with status ${response.status}: ${parseErrorMessage}`,
      response.status,
      responseText,
      undefined,
      durationMs,
    )
  }
}

export function createTransportClient({
  baseUrl = TRANSPORT_BASE,
  fetch: customFetch = fetch,
  getToken,
  getOrganizationContext,
  getWorkspaceId,
}: TransportClientOptions = {}) {
  if (!baseUrl) {
    throw new Error(
      "Transport base URL is not configured. Set COVAL_TRANSPORT_BASE_URL.",
    )
  }

  const resolvedBaseUrl = baseUrl.replace(/\/+$/, "")

  async function executeOperation(
    name: TransportOperationName,
    maybeRequestOrOptions?: unknown,
    maybeOptions?: TransportCallExecutionOptions,
  ) {
    const operation = transportOperationDefinitions[
      name
    ] as TransportOperationDefinition
    const hasRequest =
      operation.requestLocation !== "none" || operation.pathParams.length > 0
    const requestInput = hasRequest ? maybeRequestOrOptions : undefined
    const options = hasRequest
      ? maybeOptions
      : (maybeRequestOrOptions as TransportCallExecutionOptions | undefined)

    const requestRecord = getRequestRecord(requestInput)
    const operationPath = resolvePath(
      operation.path,
      operation.pathParams,
      requestRecord,
    )

    const url = new URL(
      operationPath.replace(/^\/+/, ""),
      `${resolvedBaseUrl}/`,
    )

    let token: string | null = null
    if (operation.auth === "session") {
      try {
        token = getToken ? await getToken() : null
      } catch (error: unknown) {
        throw new TransportAuthTokenError(name, error)
      }
    }

    if (operation.auth === "session" && !token) {
      throw new TransportAuthTokenError(name)
    }

    const requestHeaders = new Headers(options?.headers)

    if (token) {
      requestHeaders.set("Authorization", `Bearer ${token}`)
    }

    if (operation.organizationRequired) {
      const organizationContext = getOrganizationContext?.()
      const explicitOrganizationSlug = requestHeaders
        .get(ORGANIZATION_SLUG_HEADER)
        ?.trim()

      if (!explicitOrganizationSlug) {
        const organizationSlug = organizationContext?.slug?.trim()
        if (!organizationSlug) {
          throw new Error(
            `Transport operation "${name}" requires an organization slug.`,
          )
        }
        requestHeaders.set(ORGANIZATION_SLUG_HEADER, organizationSlug)
      } else {
        requestHeaders.set(ORGANIZATION_SLUG_HEADER, explicitOrganizationSlug)
      }
    }
    applyWorkspaceRequestHeader({ operation, requestHeaders, getWorkspaceId })

    let body: string | undefined

    if (operation.requestLocation === "query") {
      appendQueryParams(url, requestRecord as QueryParams)
    }

    if (operation.requestLocation === "json_body") {
      requestHeaders.set("Content-Type", "application/json")
      body = JSON.stringify(requestRecord)
    }

    const startedAt = getNowMs()
    const response = await customFetch(url, {
      method: operation.method,
      headers: requestHeaders,
      body,
      credentials: TRANSPORT_REQUEST_CREDENTIALS,
      signal: options?.signal,
    })
    const durationMs = getNowMs() - startedAt

    if (!response.ok) {
      const responseBody = await getErrorBody(response)
      if (isLikelyAuthRedirectResponse(operation, response)) {
        throw new TransportAuthRedirectError(
          name,
          response.status,
          responseBody,
          durationMs,
          response.url,
        )
      }
      throw new TransportRequestError(
        `Transport operation "${name}" failed with status ${response.status}.`,
        response.status,
        responseBody,
        getTransportErrorDetail(responseBody),
        durationMs,
      )
    }

    return await parseResponse(name, response, durationMs)
  }

  async function call<Name extends TransportOperationName>(
    ...args: TransportCallArgs<Name>
  ): Promise<TransportOperationResponse<Name>> {
    const [operation, maybeRequestOrOptions, maybeOptions] = args as [
      TransportOperationInput<Name>,
      (
        | TransportResolvedRequest<Name>
        | TransportCallExecutionOptions
        | undefined
      ),
      TransportCallExecutionOptions | undefined,
    ]
    const name = getTransportOperationName(operation)

    return (await executeOperation(
      name,
      maybeRequestOrOptions,
      maybeOptions,
    )) as TransportOperationResponse<Name>
  }

  const operations = Object.fromEntries(
    Object.keys(transportOperationDefinitions).map((name) => [
      name,
      (...operationArgs: unknown[]) =>
        executeOperation(
          name as TransportOperationName,
          operationArgs[0],
          operationArgs[1] as TransportCallExecutionOptions | undefined,
        ),
    ]),
  ) as TransportOperationMethods

  return {
    call,
    operations,
    definitions: transportOperationDefinitions,
  }
}
